import { useState } from 'react'
import { Tab } from '@headlessui/react'
import { ClipboardIcon, CheckIcon } from '@heroicons/react/24/outline'

interface Snippet {
  label: string
  request: string
  response: string
}

const MATCH_SNIPPET: Snippet = {
  label: 'Match names',
  request: `curl -X POST https://sportsmeld.arbitragex.pro/api/match \\
  -H "Authorization: Bearer sportsmeld_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "reference": "Manchester United",
    "names": ["Man Utd", "Man City"],
    "sport": "soccer"
  }'`,
  response: `{
  "reference": "Manchester United",
  "any_match": true,
  "details": [
    {
      "name": "Man Utd",
      "match": true,
      "confidence": 0.98,
      "method": "alias",
      "reason_code": "ALIAS_MATCH"
    },
    {
      "name": "Man City",
      "match": false,
      "confidence": 0.02,
      "method": "confusable",
      "reason_code": "CITY_RIVAL"
    }
  ],
  "processing_time_ms": 1.42
}`,
}

const EVENTS_SNIPPET: Snippet = {
  label: 'Match events',
  request: `curl -X POST https://sportsmeld.arbitragex.pro/api/match-events \\
  -H "Authorization: Bearer sportsmeld_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "sport": "soccer",
    "events": {
      "bet365":   [{ "home": "Man Utd", "away": "Liverpool" }],
      "pinnacle": [{ "home": "Manchester United", "away": "Liverpool FC" }]
    }
  }'`,
  response: `{
  "matched": [
    {
      "home": "Man Utd",
      "away": "Liverpool",
      "sources": ["bet365", "pinnacle"],
      "confidence": 0.97
    }
  ],
  "partial": [],
  "unmatched": [],
  "timings": { "total_ms": 8.2 }
}`,
}

const SNIPPETS = [MATCH_SNIPPET, EVENTS_SNIPPET]

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard unavailable (e.g. insecure context) — silently no-op.
    }
  }

  return (
    <button
      type="button"
      onClick={copy}
      aria-label={copied ? `${label} copied` : `Copy ${label}`}
      className="inline-flex items-center gap-1.5 rounded-md border border-dash-border bg-dash-surface px-2.5 py-1.5 text-xs text-dash-secondary transition-colors hover:text-dash-text focus:outline-none focus-visible:ring-2 focus-visible:ring-dash-accent"
    >
      {copied ? (
        <>
          {/* Green check = success confirmation (data semantics). */}
          <CheckIcon className="h-4 w-4 text-[#22C55E]" aria-hidden="true" />
          Copied
        </>
      ) : (
        <>
          <ClipboardIcon className="h-4 w-4" aria-hidden="true" />
          Copy
        </>
      )}
    </button>
  )
}

function CodeBlock({ title, code }: { title: string; code: string }) {
  return (
    <div className="rounded-xl border border-dash-border bg-dash-bg">
      <div className="flex items-center justify-between border-b border-dash-border px-4 py-2.5">
        <span className="font-mono text-xs uppercase tracking-wider text-dash-muted">{title}</span>
        <CopyButton text={code} label={title} />
      </div>
      <div className="overflow-x-auto p-4">
        <pre className="font-mono text-xs leading-relaxed text-[#e4e4e7]">
          <code>{code}</code>
        </pre>
      </div>
    </div>
  )
}

export function CodeExample() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-28">
      <div className="mx-auto max-w-2xl text-center">
        <h2 className="text-3xl font-bold tracking-tight text-dash-text sm:text-4xl">
          Two endpoints. Predictable JSON.
        </h2>
        <p className="mt-4 text-lg text-dash-secondary">
          Match a list of names against a reference, or reconcile whole fixture lists across sources.
        </p>
      </div>

      <div className="mx-auto mt-12 max-w-4xl">
        <Tab.Group>
          <Tab.List className="mb-5 inline-flex gap-1 rounded-lg border border-dash-border bg-dash-surface p-1">
            {SNIPPETS.map((s) => (
              <Tab
                key={s.label}
                className={({ selected }) =>
                  `rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-dash-accent ${
                    selected
                      ? 'bg-dash-bg text-dash-text'
                      : 'text-dash-secondary hover:text-dash-text'
                  }`
                }
              >
                {s.label}
              </Tab>
            ))}
          </Tab.List>
          <Tab.Panels>
            {SNIPPETS.map((s) => (
              <Tab.Panel key={s.label} className="grid gap-4 lg:grid-cols-2">
                <CodeBlock title="Request" code={s.request} />
                <CodeBlock title="Response" code={s.response} />
              </Tab.Panel>
            ))}
          </Tab.Panels>
        </Tab.Group>
      </div>
    </section>
  )
}
